/**
 * 보안 강화된 스토리지 헬퍼
 * 민감한 정보를 제외하고 필수 정보만 저장
 */

interface SafeUserData {
  id: string;
  name: string;
  username: string;
  phone_number: string;
  company_id?: string;
  branch_id?: string;
  has_admin_role: boolean;
  is_admin: boolean;
  is_active: boolean;
  is_first_login?: boolean;
}

class StorageHelper {
  private readonly USER_DATA_KEY = 'userData';
  private readonly AUTH_KEY = 'isAuthenticated';
  private readonly SESSION_TIMEOUT = 8 * 60 * 60 * 1000; // 8시간

  /**
   * 사용자 데이터 저장 (민감정보 제거)
   */
  saveUserData(userData: any): void {
    // 민감한 정보 제거
    const safeData: SafeUserData = {
      id: userData.id,
      name: userData.name,
      username: userData.username,
      phone_number: userData.phone_number,
      company_id: userData.company_id,
      branch_id: userData.branch_id,
      has_admin_role: userData.has_admin_role,
      is_admin: userData.is_admin,
      is_active: userData.is_active,
      is_first_login: userData.is_first_login,
    };

    // sessionStorage 사용 (브라우저 닫으면 자동 삭제)
    sessionStorage.setItem(this.USER_DATA_KEY, JSON.stringify(safeData));
    
    // localStorage에는 최소 정보만 저장 (자동 로그인용)
    if (this.shouldRememberUser()) {
      const minimalData = {
        username: userData.username,
        name: userData.name,
        timestamp: Date.now(),
      };
      localStorage.setItem(this.USER_DATA_KEY, JSON.stringify(minimalData));
    }
  }

  /**
   * 사용자 데이터 조회
   */
  getUserData(): SafeUserData | null {
    // 먼저 sessionStorage에서 확인
    const sessionData = sessionStorage.getItem(this.USER_DATA_KEY);
    if (sessionData) {
      try {
        return JSON.parse(sessionData);
      } catch (e) {
        console.error('Failed to parse session user data');
      }
    }

    // localStorage 확인 (자동 로그인)
    const localData = localStorage.getItem(this.USER_DATA_KEY);
    if (localData) {
      try {
        const data = JSON.parse(localData);
        // 타임스탬프 확인 (8시간 이내)
        if (data.timestamp && Date.now() - data.timestamp < this.SESSION_TIMEOUT) {
          return data;
        } else {
          // 만료된 데이터 삭제
          this.clearUserData();
        }
      } catch (e) {
        console.error('Failed to parse local user data');
      }
    }

    return null;
  }

  /**
   * 인증 상태 저장
   */
  setAuthenticated(isAuthenticated: boolean): void {
    if (isAuthenticated) {
      sessionStorage.setItem(this.AUTH_KEY, 'true');
      if (this.shouldRememberUser()) {
        localStorage.setItem(this.AUTH_KEY, 'true');
      }
    } else {
      this.clearAuth();
    }
  }

  /**
   * 인증 상태 확인
   */
  isAuthenticated(): boolean {
    return sessionStorage.getItem(this.AUTH_KEY) === 'true' || 
           localStorage.getItem(this.AUTH_KEY) === 'true';
  }

  /**
   * 자동 로그인 여부 확인
   */
  shouldRememberUser(): boolean {
    // 로그인 폼에서 "자동 로그인" 체크 여부
    // 실제 구현은 로그인 시 설정
    return localStorage.getItem('rememberMe') === 'true';
  }

  /**
   * 자동 로그인 설정
   */
  setRememberMe(remember: boolean): void {
    if (remember) {
      localStorage.setItem('rememberMe', 'true');
    } else {
      localStorage.removeItem('rememberMe');
    }
  }

  /**
   * 사용자 데이터 삭제
   */
  clearUserData(): void {
    sessionStorage.removeItem(this.USER_DATA_KEY);
    localStorage.removeItem(this.USER_DATA_KEY);
  }

  /**
   * 인증 정보 삭제
   */
  clearAuth(): void {
    sessionStorage.removeItem(this.AUTH_KEY);
    localStorage.removeItem(this.AUTH_KEY);
  }

  /**
   * 전체 데이터 삭제 (로그아웃)
   */
  clearAll(): void {
    this.clearUserData();
    this.clearAuth();
    localStorage.removeItem('rememberMe');
  }
}

export const storageHelper = new StorageHelper();
export type { SafeUserData };